import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AppLayout } from '../components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, KeyRound, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  licenseManagerAction,
  getUserOfflineComputers,
  createOfflineComputer,
  apiClient,
} from '../services/api';
import { useUserStore, useIsAdmin } from '../store/userStore';
import type { LicenseManagerDashboard as DashboardType, LicenseManagerUser } from '../types';
import i18n from '../i18n';

const BUY_LICENSE_URL = 'https://folge.me/business-license';
const BUY_LICENSE_URL_JA = 'https://jp.folge.me/business-license';
function licenseManagerErrorMessage(t: TFunction, code?: string): string {
  if (!code) return t('licenseManager.actionFailed');
  return t(`licenseManager.apiErrors.${code}`, { defaultValue: t('licenseManager.actionFailed') });
}

function licenseManagerSuccessMessage(t: TFunction, code?: string): string {
  if (!code) return t('licenseManager.licenseAssigned');
  return t(`licenseManager.apiSuccess.${code}`, { defaultValue: t('licenseManager.licenseAssigned') });
}

function generateRandomPassword(length = 15): string {
  const charset =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  return password;
}

const LicenseManagerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useUserStore((s) => s.account);
  const isAdmin = useIsAdmin();
  const logout = useUserStore((s) => s.logout);
  const [data, setData] = useState<DashboardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offlineModal, setOfflineModal] = useState<{
    open: boolean;
    userId: string | null;
    user: { fullname: string; email: string } | null;
    computers: { id: number; machine_name: string; key2: string; created_at: string }[];
    loading: boolean;
    newComputerName: string;
    key1: string;
    adding: boolean;
  }>({
    open: false,
    userId: null,
    user: null,
    computers: [],
    loading: false,
    newComputerName: '',
    key1: '',
    adding: false,
  });
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvPreview, setCsvPreview] = useState<{ name: string; email: string; password: string }[]>([]);
  const [csvRowStatus, setCsvRowStatus] = useState<Record<number, 'pending' | 'processing' | 'success' | 'error'>>({});
  const [csvRowMessage, setCsvRowMessage] = useState<Record<number, string>>({});

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.getLicenseManagerDashboard();
      setData(result);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        logout();
        navigate('/login', { replace: true });
        return;
      }
      setError(t('licenseManager.failedLoadData'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [logout, navigate, t]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const openOfflineModal = async (userId: string) => {
    setOfflineModal((prev) => ({ ...prev, open: true, userId, loading: true, computers: [], user: null }));
    try {
      const result = await getUserOfflineComputers(userId);
      setOfflineModal((prev) => ({
        ...prev,
        computers: result.computers || [],
        user: result.user || null,
        loading: false,
        newComputerName: '',
        key1: '',
      }));
    } catch {
      toast.error(t('licenseManager.failedLoadOffline'));
      setOfflineModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const addOfflineComputer = async () => {
    if (!offlineModal.userId || !offlineModal.newComputerName.trim() || !offlineModal.key1.trim()) {
      toast.error(t('licenseManager.enterComputerNameAndKey'));
      return;
    }
    setOfflineModal((prev) => ({ ...prev, adding: true }));
    try {
      const result = await createOfflineComputer({
        userId: offlineModal.userId,
        computerName: offlineModal.newComputerName.trim(),
        key1: offlineModal.key1.trim(),
      });
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setOfflineModal((prev) => ({
        ...prev,
        computers: result.computers || prev.computers,
        adding: false,
        newComputerName: '',
        key1: '',
      }));
      if (result.newComputer) {
        toast.success(t('licenseManager.offlineComputerAdded', { name: result.newComputer.machine_name }));
      }
    } catch {
      toast.error(t('licenseManager.failedAddOffline'));
      setOfflineModal((prev) => ({ ...prev, adding: false }));
    }
  };

  const parseCsv = (text: string): { name: string; email: string; password: string }[] => {
    const rows = text.split('\n');
    const users: { name: string; email: string; password: string }[] = [];
    for (const row of rows) {
      if (!row.trim()) continue;
      const cols = row.split(',').map((c) => c.trim());
      const name = cols[0] || '';
      const email = (cols[1] || '').toLowerCase();
      if (name && email) {
        users.push({ name, email, password: generateRandomPassword(15) });
      }
    }
    return users;
  };

  const onCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvPreview([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result));
      setCsvPreview(parsed);
      setCsvRowStatus({});
      setCsvRowMessage({});
    };
    reader.readAsText(file);
  };

  const createAllCsvUsers = async () => {
    if (csvPreview.length === 0) return;
    for (let i = 0; i < csvPreview.length; i++) {
      setCsvRowStatus((s) => ({ ...s, [i]: 'processing' }));
      setCsvRowMessage((m) => ({ ...m, [i]: '' }));
      try {
        const res = await licenseManagerAction({
          action: 'create_user',
          email: csvPreview[i].email,
          fullname: csvPreview[i].name,
          password: csvPreview[i].password,
          locale: i18n.language,
        });
        if (res.successCode) {
          setCsvRowStatus((s) => ({ ...s, [i]: 'success' }));
          setCsvRowMessage((m) => ({ ...m, [i]: licenseManagerSuccessMessage(t, res.successCode) }));
        } else {
          setCsvRowStatus((s) => ({ ...s, [i]: 'error' }));
          setCsvRowMessage((m) => ({ ...m, [i]: licenseManagerErrorMessage(t, res.errorCode) }));
        }
      } catch {
        setCsvRowStatus((s) => ({ ...s, [i]: 'error' }));
        setCsvRowMessage((m) => ({ ...m, [i]: t('common.requestFailed') }));
      }
    }
    await fetchDashboard();
  };

  const breadcrumbs = [{ title: t('licenseManager.breadcrumb'), isCurrentPage: true }];

  if (account && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading && !data) {
    return (
      <AppLayout showSidebar breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (error && !data) {
    return (
      <AppLayout showSidebar breadcrumbs={breadcrumbs}>
        <div className="p-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </AppLayout>
    );
  }

  const d = data!;
  const licenseUsers: LicenseManagerUser[] = d.licenseUsers || [];
  const currentLicenseUsers = licenseUsers.filter((u) => u.status === 'current');
  const pastLicenseUsers = licenseUsers.filter((u) => u.status === 'past');
  const showLastUsed = !!d.isLastUsedEnabled;
  const totalLicenses = d.licenses?.length ?? 0;
  const assignedCount = d.assignedLicensesNumber ?? 0;
  const userEmail = d.user?.email ?? '';
  const buyLicenseUrl = (() => {
    try {
      const url = new URL(i18n.language === 'ja' ? BUY_LICENSE_URL_JA : BUY_LICENSE_URL);
      if (userEmail) url.searchParams.set('email', userEmail);
      return url.toString();
    } catch {
      return BUY_LICENSE_URL;
    }
  })();

  return (
    <AppLayout showSidebar breadcrumbs={breadcrumbs}>
      <div className="space-y-6 p-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <KeyRound className="h-6 w-6" />
            {t('licenseManager.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('licenseManager.subtitle')}
          </p>
        </div>

        {/* Stats */}
        <Card className="bg-muted/40">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-muted-foreground">{t('licenseManager.totalAvailable')}</span>
                <span className="text-2xl font-semibold tabular-nums">{totalLicenses}</span>
                <span className="text-sm text-muted-foreground">{t('licenseManager.licenses')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-muted-foreground">{t('licenseManager.assigned')}</span>
                <span className="text-2xl font-semibold tabular-nums">{assignedCount}</span>
                <span className="text-sm text-muted-foreground">{t('licenseManager.seats')}</span>
              </div>
              <div className="flex-1 min-w-0" />
              <a
                href={buyLicenseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md text-sm font-semibold bg-amber-400 text-amber-950 border-2 border-amber-500 hover:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 h-9 px-4 shadow-sm"
              >
                {t('licenseManager.buyMore')}
              </a>
            </div>
            <p className="text-sm text-muted-foreground mt-4 pt-4 border-t border-border/50">
              {t('licenseManager.unassignedNote')} <strong className="text-foreground">{userEmail}</strong>
            </p>
          </CardContent>
        </Card>

        {(d.errorCode || d.successCode || d.error || d.success) && (
          <Alert
            variant={d.errorCode || d.error ? 'destructive' : 'default'}
            className="max-w-2xl"
          >
            <AlertDescription>
              {d.errorCode
                ? licenseManagerErrorMessage(t, d.errorCode)
                : d.successCode
                  ? licenseManagerSuccessMessage(t, d.successCode)
                  : d.error || d.success}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Assign seat */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('licenseManager.assignSeatTitle')}</CardTitle>
              <CardDescription>{t('licenseManager.assignSeatDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <AssignSeatForm onSuccess={fetchDashboard} />
            </CardContent>
          </Card>

          {/* Create user */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('licenseManager.createUserTitle')}</CardTitle>
              <CardDescription>{t('licenseManager.createUserDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CreateUserForm onSuccess={fetchDashboard} />
              <Button variant="outline" size="sm" onClick={() => setCsvModalOpen(true)}>
                {t('licenseManager.uploadCsv')}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* On-premise */}
        {d.isOnPremiseLicensingEnabled && d.onPremiseLicenseCode && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('licenseManager.onPremiseTitle')}</CardTitle>
              <CardDescription dangerouslySetInnerHTML={{ __html: t('licenseManager.onPremiseDescription') }} />
            </CardHeader>
            <CardContent>
              <Textarea
                readOnly
                rows={6}
                className="font-mono text-sm w-full max-w-2xl"
                value={d.onPremiseLicenseCode}
              />
            </CardContent>
          </Card>
        )}

        {/* Users table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('licenseManager.usersTableTitle')}</CardTitle>
            <CardDescription>{t('licenseManager.usersTableDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-center mt-1 mb-3">
                  {t('licenseManager.currentUsersTableTitle')}
                </h3>
                <LicenseUsersTable
                  licenseUsers={currentLicenseUsers}
                  isOfflineEnabled={!!d.isOfflineLicensingEnabled}
                  onOffline={openOfflineModal}
                  onSuccess={fetchDashboard}
                  emptyMessage={t('licenseManager.noCurrentUsersYet')}
                  defaultSortField="assigned_at"
                  defaultSortDir="desc"
                  showLastUsed={showLastUsed}
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-center mt-1 mb-3">
                  {t('licenseManager.pastUsersTableTitle')}
                </h3>
                <LicenseUsersTable
                  licenseUsers={pastLicenseUsers}
                  isOfflineEnabled={!!d.isOfflineLicensingEnabled}
                  onOffline={openOfflineModal}
                  onSuccess={fetchDashboard}
                  emptyMessage={t('licenseManager.noPastUsersYet')}
                  defaultSortField="unassigned_at"
                  defaultSortDir="desc"
                  showLastUsed={showLastUsed}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CSV Upload Modal */}
      <Dialog open={csvModalOpen} onOpenChange={setCsvModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('licenseManager.csvModalTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('licenseManager.csvModalDescription')}
          </p>
          <input type="file" accept=".csv" onChange={onCsvFileChange} className="block w-full text-sm" />
          {csvPreview.length > 0 && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead>{t('common.email')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvPreview.map((u, i) => (
                    <TableRow key={i}>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {csvRowStatus[i] === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
                        {csvRowStatus[i] === 'success' && <span className="text-green-600">{csvRowMessage[i]}</span>}
                        {csvRowStatus[i] === 'error' && <span className="text-destructive">{csvRowMessage[i]}</span>}
                        {(!csvRowStatus[i] || csvRowStatus[i] === 'pending') && <span className="text-muted-foreground">{t('common.pending')}</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex gap-2">
                <Button onClick={createAllCsvUsers}>{t('licenseManager.createAllUsers')}</Button>
                <Button variant="outline" onClick={() => setCsvModalOpen(false)}>{t('common.cancel')}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Offline computers modal */}
      <Dialog open={offlineModal.open} onOpenChange={(open) => !open && setOfflineModal((p) => ({ ...p, open: false }))}>
        <DialogContent className="max-w-4xl w-[90vw] min-h-[60vh] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {offlineModal.user
                ? t('licenseManager.offlineTitleFor', { name: offlineModal.user.fullname, email: offlineModal.user.email })
                : t('licenseManager.offlineTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('licenseManager.offlineDescription') }} />
          {offlineModal.loading ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : (
            <>
              {offlineModal.computers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('licenseManager.computerHeader')}</TableHead>
                      <TableHead>{t('licenseManager.createdHeader')}</TableHead>
                      <TableHead>{t('licenseManager.key2Header')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {offlineModal.computers.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.machine_name}</TableCell>
                        <TableCell>{new Date(c.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          {c.key2 ? (
                            <Textarea
                              readOnly
                              className="font-mono text-xs min-h-[80px]"
                              value={c.key2}
                              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                            />
                          ) : (
                            <div className="space-y-1">
                              <Textarea
                                readOnly
                                disabled
                                className="font-mono text-xs min-h-[80px] opacity-50"
                                value={c.key2 || ''}
                              />
                              <p className="text-xs text-destructive">
                                {t('licenseManager.oldActivation')}
                              </p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground">{t('licenseManager.noOfflineComputers')}</p>
              )}
              <div className="space-y-2 pt-4 border-t">
                <Label>{t('licenseManager.addNewComputer')}</Label>
                <Input
                  placeholder={t('licenseManager.computerNamePlaceholder')}
                  value={offlineModal.newComputerName}
                  onChange={(e) => setOfflineModal((p) => ({ ...p, newComputerName: e.target.value }))}
                />
                <Textarea
                  placeholder={t('licenseManager.key1Placeholder')}
                  value={offlineModal.key1}
                  onChange={(e) => setOfflineModal((p) => ({ ...p, key1: e.target.value }))}
                  className="min-h-[80px] font-mono text-sm"
                />
                <Button onClick={addOfflineComputer} disabled={offlineModal.adding}>
                  {offlineModal.adding ? <Loader2 className="h-4 w-4 animate-spin" /> : t('licenseManager.addOfflineComputer')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

function AssignSeatForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await licenseManagerAction({
        action: 'add_user',
        email: email.trim().toLowerCase(),
        send_email: sendEmail ? 1 : 0,
        locale: i18n.language,
      });
      if (res.successCode) {
        toast.success(licenseManagerSuccessMessage(t, res.successCode));
        setEmail('');
        onSuccess();
      } else {
        toast.error(licenseManagerErrorMessage(t, res.errorCode));
      }
    } catch {
      toast.error(t('common.requestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label htmlFor="assign-email">{t('common.email')}</Label>
        <Input
          id="assign-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
          className="mt-1"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        {t('licenseManager.sendEmailNotification')}
      </label>
      <Button type="submit" disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.add')}
      </Button>
    </form>
  );
}

function CreateUserForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullname.trim() || !email.trim() || !password.trim()) return;
    setSubmitting(true);
    try {
      const res = await licenseManagerAction({
        action: 'create_user',
        email: email.trim().toLowerCase(),
        fullname: fullname.trim(),
        password: password.trim(),
        locale: i18n.language,
      });
      if (res.successCode) {
        toast.success(licenseManagerSuccessMessage(t, res.successCode));
        setFullname('');
        setEmail('');
        setPassword('');
        onSuccess();
      } else {
        toast.error(licenseManagerErrorMessage(t, res.errorCode));
      }
    } catch {
      toast.error(t('common.requestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label htmlFor="create-name">{t('common.name')}</Label>
        <Input id="create-name" value={fullname} onChange={(e) => setFullname(e.target.value)} required className="mt-1" />
      </div>
      <div>
        <Label htmlFor="create-email">{t('common.email')}</Label>
        <Input id="create-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
      </div>
      <div>
        <Label htmlFor="create-password">{t('common.password')}</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="create-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={3}
            className="flex-1 min-w-[280px]"
          />
          <Button
            type="button"
            variant="outline"
            size="default"
            className="shrink-0"
            onClick={() => setPassword(generateRandomPassword(15))}
            title={t('licenseManager.generatePasswordTooltip')}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('licenseManager.generatePassword')}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t('licenseManager.credentialsNote')}</p>
      <Button type="submit" disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.create')}
      </Button>
    </form>
  );
}

function LicenseUsersTable({
  licenseUsers,
  isOfflineEnabled,
  onOffline,
  onSuccess,
  emptyMessage,
  defaultSortField = 'assigned_at',
  defaultSortDir = 'desc',
  showLastUsed,
}: {
  licenseUsers: LicenseManagerUser[];
  isOfflineEnabled: boolean;
  onOffline: (userId: string) => void;
  onSuccess: () => void;
  emptyMessage?: string;
  defaultSortField?: 'assigned_at' | 'unassigned_at' | 'last_used_at';
  defaultSortDir?: 'asc' | 'desc';
  showLastUsed: boolean;
}) {
  const { t } = useTranslation();
  const [removing, setRemoving] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'assigned_at' | 'unassigned_at' | 'last_used_at'>(
    defaultSortField
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir);

  const relTime = useMemo(() => {
    try {
      return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    } catch {
      return null;
    }
  }, []);

  const formatFullDateTime = (iso: string): string => {
    const date = new Date(iso);
    const time = date.getTime();
    if (!Number.isFinite(time)) return '—';

    // Use locale + explicit parts so "full time" shows seconds.
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  };

  const formatRelativeTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const date = new Date(iso);
    const time = date.getTime();
    if (!Number.isFinite(time)) return '—';

    const diffMs = time - Date.now(); // negative => past
    const absMs = Math.abs(diffMs);

    const second = 1000;
    const minute = 60 * second;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;

    if (!relTime) return date.toLocaleString();

    if (absMs < minute) {
      return relTime.format(Math.round(diffMs / second), 'second');
    }
    if (absMs < hour) {
      return relTime.format(Math.round(diffMs / minute), 'minute');
    }
    if (absMs < day) {
      return relTime.format(Math.round(diffMs / hour), 'hour');
    }
    if (absMs < week) {
      return relTime.format(Math.round(diffMs / day), 'day');
    }
    if (absMs < month) {
      return relTime.format(Math.round(diffMs / week), 'week');
    }
    if (absMs < year) {
      return relTime.format(Math.round(diffMs / month), 'month');
    }
    return relTime.format(Math.round(diffMs / year), 'year');
  };

  // Sort within the current table (client-side) by audit timestamps.
  const sortedUsers = useMemo(() => {
    const getTime = (
      u: LicenseManagerUser,
      field: 'assigned_at' | 'unassigned_at' | 'last_used_at'
    ): number | null => {
      const value = u[field];
      if (!value) return null;
      const time = new Date(value).getTime();
      return Number.isFinite(time) ? time : null;
    };

    const dir = sortDir;
    const field = sortField;
    return [...licenseUsers].sort((a, b) => {
      const ta = getTime(a, field);
      const tb = getTime(b, field);

      // Always push empty cells to the end.
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;

      if (ta === tb) return 0;
      return dir === 'asc' ? ta - tb : tb - ta;
    });
  }, [licenseUsers, sortDir, sortField]);

  const toggleSort = (field: 'assigned_at' | 'unassigned_at' | 'last_used_at') => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir('desc');
      return;
    }
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const handleUnassign = async (email: string) => {
    if (!window.confirm(t('licenseManager.confirmUnassign'))) return;
    setRemoving(email);
    try {
      await licenseManagerAction({ action: 'remove_user', email, locale: i18n.language });
      toast.success(t('licenseManager.licenseUnassigned'));
      onSuccess();
    } catch {
      toast.error(t('licenseManager.failedUnassign'));
    } finally {
      setRemoving(null);
    }
  };

  const handleAssign = async (email: string) => {
    setAdding(email);
    try {
      await licenseManagerAction({ action: 'add_user', email, send_email: 0, locale: i18n.language });
      toast.success(t('licenseManager.licenseAssigned'));
      onSuccess();
    } catch {
      toast.error(t('licenseManager.failedAssign'));
    } finally {
      setAdding(null);
    }
  };

  if (licenseUsers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {emptyMessage ?? t('licenseManager.noUsersYet')}
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('licenseManager.userNameHeader')}</TableHead>
          <TableHead>{t('licenseManager.userEmailHeader')}</TableHead>
          <TableHead
            onClick={() => toggleSort('assigned_at')}
            className="cursor-pointer select-none"
            aria-sort={
              sortField === 'assigned_at' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
            }
            title="Sort"
          >
            <span className="inline-flex items-center gap-1">
              {t('licenseManager.assignedAtHeader')}
              {sortField === 'assigned_at' && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
            </span>
          </TableHead>
          <TableHead
            onClick={() => toggleSort('unassigned_at')}
            className="cursor-pointer select-none"
            aria-sort={
              sortField === 'unassigned_at' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
            }
            title="Sort"
          >
            <span className="inline-flex items-center gap-1">
              {t('licenseManager.unassignedAtHeader')}
              {sortField === 'unassigned_at' && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
            </span>
          </TableHead>
          {showLastUsed && (
            <TableHead
              onClick={() => toggleSort('last_used_at')}
              className="cursor-pointer select-none"
              aria-sort={
                sortField === 'last_used_at'
                  ? sortDir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
              }
              title="Sort"
            >
              <span className="inline-flex items-center gap-1">
                {t('licenseManager.lastUsedAtHeader')}
                {sortField === 'last_used_at' && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
              </span>
            </TableHead>
          )}
          <TableHead>{t('licenseManager.actionHeader')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedUsers.map((entry) => (
          <TableRow key={`${entry.user_id}-${entry.assigned_at || ''}`}>
            <TableCell>
              <span className="font-medium">{entry.fullname}</span>
              {entry.is_admin && (
                <Badge variant="secondary" className="ml-2 text-xs">{t('licenseManager.adminBadge')}</Badge>
              )}
            </TableCell>
            <TableCell>{entry.email}</TableCell>
            <TableCell className="text-muted-foreground">
              {entry.assigned_at ? new Date(entry.assigned_at).toLocaleString() : '—'}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.unassigned_at ? new Date(entry.unassigned_at).toLocaleString() : '—'}
            </TableCell>
                {showLastUsed && (
                  <TableCell className="text-muted-foreground">
                    {entry.last_used_at ? (
                      <span title={formatFullDateTime(entry.last_used_at)}>
                        {formatRelativeTime(entry.last_used_at)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                )}
            <TableCell>
              {entry.status === 'current' ? (
                <>
                  {(!entry.is_admin || !entry.without_license) && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mr-2"
                        disabled={removing === entry.email}
                        onClick={() => handleUnassign(entry.email)}
                      >
                        {removing === entry.email ? <Loader2 className="h-4 w-4 animate-spin" /> : t('licenseManager.unassign')}
                      </Button>
                      {isOfflineEnabled && entry.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOffline(String(entry.id))}
                        >
                          {t('licenseManager.offline')}
                        </Button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  disabled={adding === entry.email}
                  onClick={() => handleAssign(entry.email)}
                >
                  {adding === entry.email ? <Loader2 className="h-4 w-4 animate-spin" /> : t('licenseManager.assign')}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default LicenseManagerPage;
